import { FormEvent, useState } from 'react';
import type { LiveFeed } from '@localagent/sdk';
import { Button } from '@localagent/ui/components/button';

export const FEED_MAX_BODY = 280;

export function FeedComposer({
  liveFeed,
  kind = 'original',
  parentId,
  quoteOfId,
  onPublished,
}: {
  liveFeed: LiveFeed;
  kind?: 'original' | 'reply' | 'quote';
  parentId?: string;
  quoteOfId?: string;
  onPublished?: () => void;
}) {
  const [body, setBody] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const runeCount = Array.from(body).length;
  const overLimit = runeCount > FEED_MAX_BODY;
  const empty = body.trim().length === 0;

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (empty) {
      setError('Write a post before publishing.');
      return;
    }
    if (overLimit) {
      setError(`Posts can be at most ${FEED_MAX_BODY} characters.`);
      return;
    }
    setPending(true);
    setError(null);
    try {
      await liveFeed.publish({
        kind,
        body: body.trim(),
        ...(parentId ? { parent_id: parentId } : {}),
        ...(quoteOfId ? { quote_of_id: quoteOfId } : {}),
      });
      setBody('');
      onPublished?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setPending(false);
    }
  }

  return (
    <form
      data-testid="feed-composer"
      onSubmit={(event) => {
        void handleSubmit(event);
      }}
      className="space-y-2"
    >
      <textarea
        data-testid="feed-composer-input"
        value={body}
        onChange={(event) => {
          setBody(event.target.value);
          setError(null);
        }}
        rows={4}
        className="w-full resize-y rounded-md bg-muted px-3 py-2 text-sm"
        placeholder="Share a short update…"
        aria-label="Write a post"
      />
      <div className="flex items-center justify-between gap-3">
        <span
          data-testid="feed-composer-count"
          className={overLimit ? 'text-xs text-destructive' : 'text-xs text-muted-foreground'}
        >
          {runeCount}/{FEED_MAX_BODY}
        </span>
        <Button
          type="submit"
          size="sm"
          data-testid="feed-composer-submit"
          disabled={pending || empty || overLimit}
        >
          {pending ? 'Posting…' : 'Post'}
        </Button>
      </div>
      {error ? (
        <p data-testid="feed-composer-error" className="text-sm text-destructive" role="alert">
          {error}
        </p>
      ) : null}
    </form>
  );
}
